// src/components/feedpost/PostPopup.js
import React, { useState } from "react";
import { formatDateTime } from "../../utils/date"; // Adjust the path as necessary
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const PostPopup = ({
  isOpen,
  onClose,
  selectedPost,
  comments,
  fetchComments,
}) => {
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  if (!isOpen || !selectedPost) return null;
  // ✅ Handle new comment submission
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setLoading(true);

    try {
      const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");
      const response = await fetch(
        `${API_BASE_URL}/comment/add-comment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify({
            post_id: selectedPost.id,
            content: newComment,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setNewComment("");
        fetchComments(selectedPost.id); // refresh comments after posting
      } else {
        alert(data.message || "Failed to add comment");
      }
    } catch (err) {
      console.error("❌ Error adding comment:", err);
      alert("An error occurred while adding the comment.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Use seller data if available, otherwise fallback to user
  const profileImage =
    selectedPost?.seller?.profile_image?.url ||
    selectedPost?.user?.profile_picture_url ||
    "/image/PostInputImage.png";

  const displayName =
    selectedPost?.seller?.name ||
    selectedPost?.user?.username ||
    "Unknown User";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-[95%] md:w-[800px] relative max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
          onClick={onClose}
        >
          ✕
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <img
            src={profileImage}

            alt="profile"
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <p className="font-semibold">
              {displayName}
            </p>
            <p className="text-xs text-gray-500">
              {formatDateTime(selectedPost?.created_at)}
            </p>
          </div>
        </div>

        {/* Content */}
        <p className="text-gray-800 mb-4">{selectedPost?.content}</p>

        {/* Media */}
        {selectedPost?.media_url?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {selectedPost.media_url.map((media, idx) =>
              media.media_type === "video" ? (
                <video
                  key={idx}
                  src={media.url}
                  controls
                  className="rounded-lg shadow-md object-cover w-full h-[250px] w-full"
                />
              ) : (
                <img
                  key={idx}
                  src={media.url}
                  alt={`media-${idx}`}
                  className="rounded-lg shadow-md object-cover w-full h-[250px] w-full"
                />
              )
            )}
          </div>
        )}

        {/* Comments */}
        <div className="mt-4">
          <h3 className="font-semibold mb-2">Comments</h3>
          {comments?.length > 0 ? (
            comments.map((comment, idx) => (
              <div
                key={idx}
                className="border-b border-gray-200 py-2 text-sm"
              >
                <p className="font-medium">{comment?.user?.username}</p>
                <p className="text-gray-600">{comment?.content}</p>
              </div>
            ))
          ) : (
            <p className="text-gray-500">No comments yet.</p>
          )}
        </div>
        {/* ✅ Add Comment Box */}
        <div className="mt-4 flex items-center gap-2">
          <input
            type="text"
            placeholder="Write a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="flex-grow border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
          />
          <button
            onClick={handleAddComment}
            disabled={loading}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? "Posting..." : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostPopup;
